import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import quickTripLogo from '../../../assets/imgs/quick_trip_logo.svg?react';
import BuyTicketList from '../../../components/BuyTicketList/BuyTicketList';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import FujiModal from '../../../components/fuji-modal/FujiModal';
import { showNotificationDialog } from '../../../utils';
import styles from './styles.module.css';
import useR05Api from '../../../api/useR05Api';
import { APP_ID, HOME_PAGE_URL, TIMEOUT_DURATION } from '../../../utils/Constants';
import { TicketInfo } from 'types';
import { ErrorType } from 'types/errors';

/**
 * 図1 販売中チケット一覧画面
 */
const BuyTicketListScreen = () => {
    const [loading, setLoading] = useState(false);
    const [ticketList, setTicketList] = useState<TicketInfo[]>([]);
    const [groupTicketList, setGroupTicketList] = useState([]);
    const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
    const { t } = useTranslation();
    const { fetchTicketList, fetchGroupTicketList } = useR05Api();
    const [searchParams] = useSearchParams();

    const { appTopImage } = JSON.parse(localStorage.getItem('APP_CONFIG') ?? '{}');
    const saleTicketGroupId = useMemo(() => searchParams.get('saleTicketGroupId') ?? undefined, [searchParams]);

    const showErrorMessage = useCallback(
        (error: ErrorType) => {
            switch (error.errorType) {
                case 'undefined_parameter': //4.x.xのエラー
                case 'invalid_api_key':
                    showNotificationDialog(
                        'error',
                        '',
                        t('notification.title'),
                        t('error-message.tickets.fetch-failed'),
                    );
                    break;
                case 'unexpected': //5.x.xのエラー
                    showNotificationDialog(
                        'error',
                        '',
                        t('notification.title'),
                        t('error-message.tickets.unexpected-error'),
                    );
                    break;

                case 'not_found_sale_ticket_group':
                    setIsOpenConfirmModal(true);
                    break;

                default:
                    showNotificationDialog(
                        'error',
                        '',
                        t('notification.title'),
                        t('error-message.tickets.unexpected-error'),
                    );
                    break;
            }
        },
        [t],
    );

    //「ホーム」リンクへ遷移する
    useLayoutEffect(() => {
        if (HOME_PAGE_URL && !saleTicketGroupId) {
            window.location.replace(HOME_PAGE_URL);
        }
    }, [saleTicketGroupId]);

    //チケット一覧の情報を取得する
    useEffect(() => {
        if (!navigator.onLine) {
            showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
            return;
        }

        void (async () => {
            let timeoutId;

            try {
                timeoutId = setTimeout(() => {
                    setLoading(false);
                    showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
                }, TIMEOUT_DURATION);

                setLoading(true);
                const ticketList = await fetchTicketList({
                    appId: APP_ID,
                    saleTicketGroupId: saleTicketGroupId,
                });
                // const groupTicketList = await fetchGroupTicketList({ appId: APP_ID });
                clearTimeout(timeoutId);
                setLoading(false);
                setTicketList(ticketList);
                setGroupTicketList(groupTicketList);
            } catch (error) {
                setLoading(false);
                clearTimeout(timeoutId);
                showErrorMessage(error as ErrorType);
            }
        })();
    }, [t, fetchGroupTicketList, fetchTicketList, showErrorMessage, saleTicketGroupId]);

    return (
        <>
            {loading && <CenterLoading />}
            <FujiModal
                body={t('tickets.error-no-group-ticket')}
                txtSubmit={'OK'}
                isOpen={isOpenConfirmModal}
                onSubmit={() => {
                    setIsOpenConfirmModal(false);
                }}
            />
            <div className="content-header-container scrollable">
                <div className={styles.logo}>{React.createElement(quickTripLogo, { className: styles.logoIcon })}</div>
                {appTopImage && (
                    <div className={styles.imgContainer}>
                        <img src={appTopImage} alt="" />
                    </div>
                )}
                <p className={styles.title}>{t('tickets.title')}</p>
                <div className={styles.ticketList}>
                    {navigator.onLine ? (
                        <BuyTicketList ticketList={ticketList} groupTicketList={groupTicketList} isLoading={loading} />
                    ) : (
                        <div className={styles.offlineMessage}>{t('tickets.offline-no-ticket')}</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BuyTicketListScreen;
